import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "@tanstack/react-router";
import { CARDS, type Column, type GameCommand, type PlayerSide, type PublicGameState, type PublicTeam, type RoomSnapshot } from "@soccertcg/game-engine";
import { api } from "../api.ts";
import { Chat } from "../components/Chat.tsx";
import { GameControls, HandFan, type DeploymentSelection } from "../components/GameControls.tsx";
import { Pitch } from "../components/Pitch.tsx";

interface MatchMoment { key: number; kind: "turn" | "goal"; title: string; sub?: string }

function TeamPlate({ side, team }: { side: PlayerSide; team?: PublicTeam }) {
  const keeperDef = team ? CARDS[team.goalkeeper.definitionId] : undefined;
  const maxHealth = keeperDef?.health ?? 1;
  const health = team?.goalkeeper.health ?? 0;
  return <div className={`mt-plate mt-${side}`}>
    <small>{side === "home" ? "HOME" : "AWAY"}</small>
    <strong>{team?.name ?? "Waiting…"}</strong>
    {team && <span className="mt-meta">
      <span className="mt-hp" title={`Goalkeeper ${health}/${maxHealth} HP`}><i style={{ width: `${Math.max(0, Math.min(100, (health / maxHealth) * 100))}%` }}/><b>{health}</b> GK</span>
      <span title="Cards in hand">✋ {team.handCount}</span>
      <span title="Cards left in deck">🂠 {team.deckCount}</span>
    </span>}
  </div>;
}

export function RoomPage() {
  const { roomId } = useParams({ from: "/rooms/$roomId" });
  const [snapshot, setSnapshot] = useState<RoomSnapshot>();
  const [error, setError] = useState("");
  const [selectedId, setSelectedId] = useState<string>();
  const [selectedHandId, setSelectedHandId] = useState<string>();
  const [deployment, setDeployment] = useState<DeploymentSelection>();
  const [replayIndex, setReplayIndex] = useState<number>();
  const [moment, setMoment] = useState<MatchMoment>();
  const [resultDismissed, setResultDismissed] = useState(false);
  const [sending, setSending] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const momentRef = useRef<{ side?: PlayerSide; ballKind?: string; initialized: boolean }>({ initialized: false });
  const refresh = useCallback(() => api.room(roomId).then(setSnapshot).catch((e: Error) => setError(e.message)), [roomId]);

  // Broadcast-style match moments: turn hand-offs and goals on the live state.
  useEffect(() => {
    if (!snapshot) return;
    const state = snapshot.state;
    const prev = momentRef.current;
    momentRef.current = { side: state.activeSide, ballKind: state.ball.kind, initialized: true };
    if (!prev.initialized) return;
    if (state.ball.kind === "goal" && prev.ballKind !== "goal") {
      setMoment({ key: Date.now(), kind: "goal", title: "GOAL!", sub: state.lastAction });
    } else if (state.status === "active" && state.activeSide !== prev.side) {
      const name = state.teams[state.activeSide]?.name ?? "";
      const mine = snapshot.viewerSide === state.activeSide;
      setMoment({ key: Date.now(), kind: "turn", title: mine ? "YOUR TURN" : `${name} TO PLAY`, sub: `TURN ${state.turn}` });
    }
  }, [snapshot]);

  useEffect(() => {
    if (!moment) return;
    const timer = setTimeout(() => setMoment(undefined), moment.kind === "goal" ? 2600 : 1500);
    return () => clearTimeout(timer);
  }, [moment]);

  useEffect(() => {
    let active = true;
    void refresh();
    const socket = api.socket(roomId);
    socket.onmessage = () => { if (active) void refresh(); };
    socket.onerror = () => { if (active) setError("Live connection interrupted; reconnecting on refresh."); };
    return () => { active = false; socket.close(); };
  }, [refresh, roomId]);

  useEffect(() => {
    const closeSelection = (event: KeyboardEvent) => { if (event.key === "Escape") clearSelection(); };
    window.addEventListener("keydown", closeSelection);
    return () => window.removeEventListener("keydown", closeSelection);
  }, []);

  const displayState: PublicGameState | undefined = useMemo(
    () => replayIndex === undefined ? snapshot?.state : snapshot?.replay[replayIndex]?.state,
    [snapshot, replayIndex],
  );

  async function send(command: GameCommand): Promise<boolean> {
    if (sending) return false;
    setError("");
    setSending(true);
    try {
      await api.command(roomId, command);
      clearSelection();
      await refresh();
      return true;
    } catch (cause) {
      setError((cause as Error).message);
      return false;
    } finally {
      setSending(false);
    }
  }

  async function sendChat(message: string, author: string) {
    try {
      await api.chat(roomId, message, author);
      await refresh();
    } catch (cause) {
      setError((cause as Error).message);
    }
  }

  function clearSelection() {
    setSelectedId(undefined);
    setSelectedHandId(undefined);
    setDeployment(undefined);
  }

  if (!snapshot || !displayState) return <main className="center-page"><div className="loader-ball">●</div><p>{error || "Walking out onto the pitch…"}</p><a href="/">Back to lobby</a></main>;

  const home = displayState.teams.home;
  const away = displayState.teams.away;
  const viewerSide = snapshot.viewerSide;
  const canControl = Boolean(viewerSide && displayState.status === "active" && replayIndex === undefined);
  const hand = viewerSide ? displayState.teams[viewerSide]?.hand ?? [] : [];
  const mine = viewerSide ? displayState.field.filter(player => player.controller === viewerSide) : [];
  const carrierId = displayState.ball.kind === "player" ? displayState.ball.playerId : undefined;
  const deploymentCard = deployment && deployment.kind !== "mulligan" ? hand.find(card => card.instanceId === deployment.cardId) : undefined;
  const substitutionCost = deploymentCard ? CARDS[deploymentCard.definitionId]!.substitutions : 0;
  const selectablePlayerIds = deployment?.kind === "substitute"
    ? mine.filter(player => player.instanceId !== carrierId).map(player => player.instanceId)
    : [];
  const selectableColumns = deployment?.kind === "summon"
    ? ([0, 1, 2] as Column[]).filter(column => mine.length < 3 && !mine.some(player => player.zone.column === column))
    : [];

  function selectHand(cardId: string) {
    if (deployment?.kind === "mulligan") {
      const selected = deployment.cardIds.includes(cardId);
      const deckCount = viewerSide ? displayState?.teams[viewerSide]?.deckCount ?? 0 : 0;
      if (!selected && deployment.cardIds.length >= deckCount) return;
      setDeployment({ kind: "mulligan", cardIds: selected ? deployment.cardIds.filter(id => id !== cardId) : [...deployment.cardIds, cardId] });
      return;
    }
    setSelectedHandId(cardId);
    setSelectedId(undefined);
    setDeployment(undefined);
  }

  function selectPitch(id: string) {
    if (deployment?.kind === "substitute") {
      if (!selectablePlayerIds.includes(id)) return;
      const isSelected = deployment.removePlayerIds.includes(id);
      const removePlayerIds = isSelected
        ? deployment.removePlayerIds.filter(playerId => playerId !== id)
        : deployment.removePlayerIds.length < substitutionCost
          ? [...deployment.removePlayerIds, id]
          : [...deployment.removePlayerIds.slice(0, -1), id];
      setDeployment({ ...deployment, removePlayerIds });
      return;
    }
    if (deployment) return;
    setSelectedId(id);
    setSelectedHandId(undefined);
  }

  function showReplay(index?: number) {
    clearSelection();
    setReplayIndex(index);
  }

  return <div className="game-shell fullscreen-game">
    <a className="game-exit" href="/" aria-label="Back to clubhouse" title="Back to clubhouse">‹</a>
    <button className={`game-chat-toggle ${showChat ? "active" : ""}`} onClick={() => setShowChat(value => !value)}>Chat <b>{snapshot.chat.length}</b></button>
    {sending && <div className="command-pending" role="status"><i/> Resolving play…</div>}
    {error && <div className="error-banner room-error">{error}<button onClick={() => setError("")}>×</button></div>}
    <main className={`game-layout ${showChat ? "chat-open" : ""}`}>
      <section className="board-column">
        <div className="match-topbar">
          <TeamPlate side="away" team={away}/>
          <div className="mt-center">
            <em>{snapshot.roomName}</em>
            <span>TURN {displayState.turn}{snapshot.aiDifficulty ? ` · ${snapshot.aiDifficulty === "easy" ? "ACADEMY" : snapshot.aiDifficulty === "medium" ? "TACTICAL" : "WORLD CLASS"} AI` : ""}</span>
            <b className={displayState.status === "active" && (displayState.reaction ? viewerSide === displayState.reaction.offeredTo : viewerSide === displayState.activeSide) ? "mt-yours" : ""}>{displayState.status === "complete" ? `${displayState.teams[displayState.winner!]?.name} wins` : displayState.status === "waiting" ? "Awaiting opponent" : displayState.reaction ? viewerSide === displayState.reaction.offeredTo ? "You to react" : `${displayState.teams[displayState.reaction.offeredTo]?.name ?? "—"} to react` : viewerSide === displayState.activeSide ? "You to play" : `${displayState.teams[displayState.activeSide]?.name ?? "—"} to play`}</b>
          </div>
          <TeamPlate side="home" team={home}/>
        </div>
        <div className="last-action"><span>{replayIndex === undefined ? "LATEST" : `EVENT ${snapshot.replay[replayIndex]?.seq}`}</span>{replayIndex === undefined ? displayState.lastAction : snapshot.replay[replayIndex]?.summary}</div>
        {displayState.effectLog.length > 0 && <div className="effect-feed">{displayState.effectLog.slice(-3).map((entry, index) => <span key={`${entry}-${index}`}>✦ {entry}</span>)}</div>}
        <Pitch
          state={displayState}
          selectedId={selectedId}
          markedIds={deployment?.kind === "substitute" ? deployment.removePlayerIds : []}
          selectableIds={selectablePlayerIds}
          onSelect={selectPitch}
          hand={canControl && viewerSide ? <HandFan hand={hand} side={viewerSide} selectedId={selectedHandId} markedIds={deployment?.kind === "mulligan" ? deployment.cardIds : []} onSelect={selectHand}/> : undefined}
          handSide={canControl ? viewerSide : undefined}
          slotSide={deployment?.kind === "summon" ? viewerSide : undefined}
          selectableColumns={selectableColumns}
          selectedColumn={deployment?.kind === "summon" ? deployment.column : undefined}
          onSlotSelect={column => deployment?.kind === "summon" && setDeployment({ ...deployment, column })}
        />
        {canControl && viewerSide && <GameControls
          state={displayState}
          side={viewerSide}
          selectedId={selectedId}
          selectedHandId={selectedHandId}
          deployment={deployment}
          setDeployment={setDeployment}
          clearSelection={clearSelection}
          send={send}
        />}
        {snapshot.replay.length > 0 && <div className="replay-bar panel"><button className={replayIndex === undefined ? "active" : ""} onClick={() => showReplay()}>Live</button><input type="range" min={0} max={snapshot.replay.length - 1} value={replayIndex ?? snapshot.replay.length - 1} onChange={event => showReplay(Number(event.target.value))}/><span>{replayIndex === undefined ? `${snapshot.replay.length} events` : `${replayIndex + 1} / ${snapshot.replay.length}`}</span></div>}
        {moment && replayIndex === undefined && <div className={`match-moment moment-${moment.kind}`} key={moment.key} aria-live="polite"><div className="moment-card"><h1>{moment.title}</h1>{moment.sub && <p>{moment.sub}</p>}</div></div>}
        {displayState.status === "complete" && !resultDismissed && replayIndex === undefined && <div className="winner-splash match-full-time">
          <span>FULL TIME</span>
          <h1>{displayState.teams[displayState.winner!]?.name ?? "Match over"}</h1>
          <p>{viewerSide ? (displayState.winner === viewerSide ? "takes the trophy — that's you" : "wins after the final shot") : "wins after the final shot"}</p>
          <div className="full-time-actions">
            <button className="button primary" onClick={() => { setResultDismissed(true); showReplay(0); }}>Watch replay</button>
            <button className="button ghost" onClick={() => setResultDismissed(true)}>View final board</button>
            <a className="button ghost" href="/">Back to clubhouse</a>
          </div>
        </div>}
      </section>
      {showChat && <Chat snapshot={snapshot} onSend={sendChat}/>}
    </main>
  </div>;
}
